/**
 * Data Export Utilities
 * Provides functions to export data in various formats (JSON, XML, XLSX)
 * Supports nested data from OData $expand (e.g., LineRefs, OperationRefs)
 */

import * as XLSX from 'xlsx';

export type ExportFormat = 'json' | 'xml' | 'xlsx';

interface ExportOptions {
  filename: string;
  data: Record<string, unknown>[];
  format: ExportFormat;
}

// Known Ref fields that contain nested arrays from OData $expand
const REF_FIELDS = [
  'LineRefs', 'ActualDeliveryLineRefs', 'ActualReceiptLineRefs', 'WarehouseOrderLineRefs',
  'OperationRefs', 'MaterialRefs', 'EstimatedMaterialRefs',
  'OrderLines', 'InvoiceLines', 'ItemPrices', 'ItemInventory', 'BillOfMaterials',
  'Details', 'RelatedItems'
];

/**
 * Check if a field is a Ref/nested array field
 */
function isRefField(key: string): boolean {
  return REF_FIELDS.includes(key) || key.endsWith('Refs') || key.endsWith('Lines');
}

/**
 * Extract all nested Ref data from records, grouped by Ref type
 */
function extractNestedData(data: Record<string, unknown>[]): Map<string, Record<string, unknown>[]> {
  const nestedDataMap = new Map<string, Record<string, unknown>[]>();

  data.forEach((row, rowIndex) => {
    // Find primary key field (usually first field or one ending with Order/Id)
    const primaryKey = findPrimaryKey(row);
    const primaryValue = row[primaryKey];

    Object.entries(row).forEach(([key, value]) => {
      if (isRefField(key) && Array.isArray(value) && value.length > 0) {
        if (!nestedDataMap.has(key)) {
          nestedDataMap.set(key, []);
        }

        // Add parent reference to each nested record
        value.forEach((nestedRecord: Record<string, unknown>) => {
          nestedDataMap.get(key)!.push({
            _ParentRowIndex: rowIndex + 1,
            _ParentKey: primaryValue,
            ...nestedRecord
          });
        });
      }
    });
  });

  return nestedDataMap;
}

/**
 * Find the primary key field name in a record
 */
function findPrimaryKey(row: Record<string, unknown>): string {
  const keys = Object.keys(row);
  // Common primary key patterns
  const primaryKeyPatterns = ['SalesOrder', 'Order', 'ProductionOrder', 'Item', 'Id', 'ID'];

  for (const pattern of primaryKeyPatterns) {
    const match = keys.find(k => k === pattern || k.endsWith(pattern));
    if (match) return match;
  }

  // Return first non-Ref field
  return keys.find(k => !isRefField(k)) || keys[0];
}

/**
 * Flatten nested data for simpler export (removes Ref arrays, keeps scalar values)
 */
function flattenForExport(data: Record<string, unknown>[]): Record<string, unknown>[] {
  return data.map(row => {
    const flatRow: Record<string, unknown> = {};

    Object.entries(row).forEach(([key, value]) => {
      if (isRefField(key) && Array.isArray(value)) {
        // Store count of nested items instead of full array
        flatRow[`${key}_Count`] = value.length;
      } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Flatten nested objects
        flatRow[key] = JSON.stringify(value);
      } else {
        flatRow[key] = value;
      }
    });

    return flatRow;
  });
}

/**
 * Trigger download of a file
 */
function downloadFile(content: string | Blob, filename: string, mimeType: string) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Convert data to JSON and download
 * Includes all nested Ref data in the JSON structure
 */
export function exportToJson(data: Record<string, unknown>[], filename: string) {
  // JSON naturally handles nested objects/arrays, so we export as-is
  const jsonContent = JSON.stringify(data, null, 2);
  downloadFile(jsonContent, `${filename}.json`, 'application/json');
}

/**
 * Convert data to XML and download
 * Includes nested Ref data as child elements
 */
export function exportToXml(data: Record<string, unknown>[], filename: string) {
  const xmlContent = convertToXml(data);
  downloadFile(xmlContent, `${filename}.xml`, 'application/xml');
}

/**
 * Convert array of objects to XML string
 * Properly handles nested arrays (Ref data)
 */
function convertToXml(data: Record<string, unknown>[]): string {
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<data>\n';

  data.forEach((item, index) => {
    xml += `  <record id="${index + 1}">\n`;
    Object.entries(item).forEach(([key, value]) => {
      const safeKey = key.replace(/[^a-zA-Z0-9_]/g, '_');

      if (isRefField(key) && Array.isArray(value)) {
        // Handle nested Ref arrays
        xml += `    <${safeKey}>\n`;
        value.forEach((nestedItem: Record<string, unknown>, nestedIndex: number) => {
          xml += `      <item index="${nestedIndex + 1}">\n`;
          Object.entries(nestedItem).forEach(([nestedKey, nestedValue]) => {
            const safeNestedKey = nestedKey.replace(/[^a-zA-Z0-9_]/g, '_');
            const safeNestedValue = escapeXml(formatValueForXml(nestedValue));
            xml += `        <${safeNestedKey}>${safeNestedValue}</${safeNestedKey}>\n`;
          });
          xml += `      </item>\n`;
        });
        xml += `    </${safeKey}>\n`;
      } else {
        const safeValue = escapeXml(formatValueForXml(value));
        xml += `    <${safeKey}>${safeValue}</${safeKey}>\n`;
      }
    });
    xml += '  </record>\n';
  });

  xml += '</data>';
  return xml;
}

/**
 * Format a value for XML output
 */
function formatValueForXml(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Escape special XML characters
 */
function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Convert data to XLSX and download
 * Creates multiple sheets: Main data + separate sheets for each Ref type
 */
export function exportToXlsx(data: Record<string, unknown>[], filename: string) {
  // Create workbook
  const workbook = XLSX.utils.book_new();

  // Extract nested Ref data
  const nestedDataMap = extractNestedData(data);

  // Flatten main data for the primary sheet (removes nested arrays)
  const flattenedData = flattenForExport(data);

  // Create main data worksheet
  const mainWorksheet = XLSX.utils.json_to_sheet(flattenedData);
  mainWorksheet['!cols'] = getColumnWidths(flattenedData);
  XLSX.utils.book_append_sheet(workbook, mainWorksheet, 'Data');

  // Create separate sheets for each Ref type
  nestedDataMap.forEach((nestedRecords, refName) => {
    if (nestedRecords.length > 0) {
      // Truncate sheet name to max 31 chars (Excel limit)
      const sheetName = refName.length > 31 ? refName.substring(0, 31) : refName;

      const nestedWorksheet = XLSX.utils.json_to_sheet(nestedRecords);
      nestedWorksheet['!cols'] = getColumnWidths(nestedRecords);
      XLSX.utils.book_append_sheet(workbook, nestedWorksheet, sheetName);
    }
  });

  // Generate buffer and download
  const xlsxBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([xlsxBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  downloadFile(blob, `${filename}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

/**
 * Calculate optimal column widths based on data
 */
function getColumnWidths(data: Record<string, unknown>[]): { wch: number }[] {
  if (data.length === 0) return [];

  const headers = Object.keys(data[0]);

  return headers.map(header => {
    // Get max length of header and all values in column
    let maxLength = header.length;

    data.forEach(row => {
      const value = String(row[header] ?? '');
      if (value.length > maxLength) {
        maxLength = value.length;
      }
    });

    // Add some padding and cap at reasonable max
    return { wch: Math.min(maxLength + 2, 50) };
  });
}

/**
 * Main export function - exports data in specified format
 */
export function exportData({ filename, data, format }: ExportOptions) {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Clean filename (remove special characters)
  const cleanFilename = filename.replace(/[^a-zA-Z0-9_-]/g, '_');

  switch (format) {
    case 'json':
      exportToJson(data, cleanFilename);
      break;
    case 'xml':
      exportToXml(data, cleanFilename);
      break;
    case 'xlsx':
      exportToXlsx(data, cleanFilename);
      break;
    default:
      console.error(`Unknown export format: ${format}`);
  }
}

/**
 * Get file extension for format
 */
export function getFileExtension(format: ExportFormat): string {
  return format;
}

/**
 * Get MIME type for format
 */
export function getMimeType(format: ExportFormat): string {
  switch (format) {
    case 'json':
      return 'application/json';
    case 'xml':
      return 'application/xml';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    default:
      return 'application/octet-stream';
  }
}
